import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Search, 
  Eye,
  User,
  CreditCard,
  TrendingUp
} from 'lucide-react';
import { TransactionService } from '../../services/transactionService';
import { UserService } from '../../services/userService';
import LoadingSpinner from '../common/LoadingSpinner';
import { useNotification } from '../common/Notification';

const AdminTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const { addNotification } = useNotification();

  useEffect(() => {
    loadTransactions();
  }, []);

  useEffect(() => {
    filterTransactions();
  }, [transactions, searchTerm, filterType, filterStatus, dateRange]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      
      // Charger toutes les transactions
      const allTransactions = await TransactionService.getAllTransactions();
      
      // Charger les informations des utilisateurs
      const userIds = [...new Set(allTransactions.map(t => t.userId))];
      const usersData = {};
      
      await Promise.all(
        userIds.map(async (userId) => {
          try {
            const user = await UserService.getUserById(userId);
            usersData[userId] = user;
          } catch (error) {
            console.error(`Erreur lors du chargement de l'utilisateur ${userId}:`, error);
            usersData[userId] = { firstName: 'Utilisateur', lastName: 'Inconnu', email: 'N/A' };
          }
        })
      );
      
      setUsers(usersData);
      setTransactions(allTransactions);
    } catch (error) {
      console.error('Erreur lors du chargement des transactions:', error);
      addNotification('error', 'Erreur de chargement', 'Impossible de charger les transactions');
    } finally {
      setLoading(false);
    }
  };

  const filterTransactions = () => {
    let filtered = [...transactions];

    // Filtrer par terme de recherche
    if (searchTerm) {
      filtered = filtered.filter(transaction => {
        const user = users[transaction.userId] || {};
        const userName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
        const userEmail = (user.email || '').toLowerCase();
        const productName = (transaction.productName || '').toLowerCase();
        const description = (transaction.description || '').toLowerCase();
        
        return userName.includes(searchTerm.toLowerCase()) ||
               userEmail.includes(searchTerm.toLowerCase()) ||
               productName.includes(searchTerm.toLowerCase()) ||
               description.includes(searchTerm.toLowerCase()) ||
               transaction.id.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Filtrer par type
    if (filterType !== 'all') {
      filtered = filtered.filter(transaction => transaction.type === filterType);
    }

    // Filtrer par statut
    if (filterStatus !== 'all') {
      filtered = filtered.filter(transaction => transaction.status === filterStatus);
    }

    // Filtrer par date
    if (dateRange.start) {
      filtered = filtered.filter(transaction => {
        const transactionDate = new Date(transaction.createdAt);
        const startDate = new Date(dateRange.start);
        return transactionDate >= startDate;
      });
    }

    if (dateRange.end) {
      filtered = filtered.filter(transaction => {
        const transactionDate = new Date(transaction.createdAt);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999);
        return transactionDate <= endDate;
      });
    }

    // Trier par date (plus récent en premier)
    filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    setFilteredTransactions(filtered);
  };

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
  };

  const closeModal = () => {
    setSelectedTransaction(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'recharge':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'purchase':
        return <CreditCard className="w-4 h-4" />;
      default:
        return <CreditCard className="w-4 h-4" />;
    }
  };

  const getTransactionTypeLabel = (transaction) => {
    if (transaction.type === 'recharge') {
      return TransactionService.getBalanceMovementLabel(transaction);
    }

    return 'Achat';
  };

  const getTypeBadge = (transaction) => {
    const badges = {
      recharge: 'bg-green-100 text-green-800',
      purchase: 'bg-purple-100 text-purple-800'
    };

    const type = transaction.type;
    const rechargeClass = transaction.direction === 'debit'
      ? transaction.method === 'cash'
        ? 'bg-cyan-100 text-cyan-800'
        : transaction.method === 'payment'
          ? 'bg-amber-100 text-amber-800'
          : 'bg-slate-100 text-slate-800'
      : transaction.method === 'cash'
        ? 'bg-sky-100 text-sky-800'
        : transaction.method === 'payment'
          ? 'bg-emerald-100 text-emerald-800'
          : 'bg-green-100 text-green-800';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        type === 'recharge' ? rechargeClass : (badges[type] || 'bg-gray-100 text-gray-800')
      }`}>
        {getTypeIcon(type)}
        <span className="ml-1">{getTransactionTypeLabel(transaction)}</span>
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      validated: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-green-100 text-green-800'
    };
    
    const labels = {
      pending: 'En attente',
      validated: 'Validé',
      rejected: 'Rejeté',
      completed: 'Terminé'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        badges[status] || 'bg-gray-100 text-gray-800'
      }`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link
                to="/admin"
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <h1 className="text-xl font-semibold text-gray-900">
                Toutes les transactions
              </h1>
            </div>
            <div className="text-sm text-gray-500">
              {filteredTransactions.length} transaction(s)
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher par utilisateur, produit, ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Type Filter */}
            <div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">Tous les types</option>
                <option value="recharge">Recharge</option>
                <option value="purchase">Achat</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="validated">Validé</option>
                <option value="completed">Terminé</option>
                <option value="rejected">Rejeté</option>
              </select>
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de début
              </label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de fin
              </label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransactions.map((transaction) => {
                  const user = users[transaction.userId] || {};
                  return (
                    <tr 
                      key={transaction.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleTransactionClick(transaction)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                              <User className="w-4 h-4 text-primary-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {transaction.productName || transaction.description}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {transaction.id.substring(0, 8)}...
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getTypeBadge(transaction)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {(transaction.type === 'recharge' && transaction.direction === 'debit' ? '-' : '') + formatCurrency(transaction.amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(transaction.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(transaction.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTransactionClick(transaction);
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredTransactions.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-500">
                Aucune transaction trouvée
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Transaction Modal */}
      {selectedTransaction && (
        <div>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Détails de la transaction
                  </h3>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    ×
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Informations de la transaction</h4>
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm text-gray-500">ID:</span>
                        <div className="mt-1 text-sm text-gray-900 font-mono">{selectedTransaction.id}</div>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Type:</span>
                        <div className="mt-1">{getTypeBadge(selectedTransaction)}</div>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Montant:</span>
                        <div className="mt-1 text-lg font-semibold text-gray-900">
                          {(selectedTransaction.type === 'recharge' && selectedTransaction.direction === 'debit' ? '-' : '') + formatCurrency(selectedTransaction.amount)}
                        </div>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Statut:</span>
                        <div className="mt-1">{getStatusBadge(selectedTransaction.status)}</div>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Date:</span>
                        <div className="mt-1 text-sm text-gray-900">
                          {formatDate(selectedTransaction.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Informations utilisateur</h4>
                    <div className="space-y-3">
                      {users[selectedTransaction.userId] && (
                        <>
                          <div>
                            <span className="text-sm text-gray-500">Nom:</span>
                            <div className="mt-1 text-sm text-gray-900">
                              {users[selectedTransaction.userId].firstName} {users[selectedTransaction.userId].lastName}
                            </div>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">Email:</span>
                            <div className="mt-1 text-sm text-gray-900">
                              {users[selectedTransaction.userId].email}
                            </div>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">Entreprise:</span>
                            <div className="mt-1 text-sm text-gray-900">
                              {users[selectedTransaction.userId].company || 'N/A'}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {selectedTransaction.description && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Description</h4>
                    <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                      {selectedTransaction.description}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTransactions;
